import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePortal } from './PortalContext';
import AddressAutocomplete from '../components/AddressAutocomplete';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function safeDecodeParam(v) {
  if (v == null || v === '') return '';
  try {
    return decodeURIComponent(v.replace(/\+/g, ' '));
  } catch {
    return v;
  }
}

const PortalLogin = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorIsInfo, setErrorIsInfo] = useState(false);
  const [multipleMatches, setMultipleMatches] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, refreshSession } = usePortal();
  const autoLoginAttempted = useRef(false);

  // Pre-fill email, name, address from URL (e.g. quote email links)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    const nameParam = searchParams.get('name');
    const addressParam = searchParams.get('address');
    if (emailParam) setEmail(safeDecodeParam(emailParam));
    if (nameParam) setName(safeDecodeParam(nameParam));
    if (addressParam) setAddress(safeDecodeParam(addressParam));
  }, [searchParams]);

  // One-tap sign-in when ?auto=1 and name, email, address are present (from emailed portal link)
  useEffect(() => {
    const auto = searchParams.get('auto') === '1' || searchParams.get('auto') === 'true';
    if (!auto || autoLoginAttempted.current) return;
    const emailParam = searchParams.get('email');
    const nameParam = searchParams.get('name');
    const addressParam = searchParams.get('address');
    if (!emailParam || !nameParam || !addressParam) return;
    const em = safeDecodeParam(emailParam).trim();
    const nm = safeDecodeParam(nameParam).trim();
    const ad = safeDecodeParam(addressParam).trim();
    if (!em.includes('@') || !nm || !ad) return;
    autoLoginAttempted.current = true;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${BASE_URL}/api/portal/direct-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name: nm, address: ad, email: em }),
        });
        let data = {};
        try {
          data = await response.json();
        } catch (_) {}
        if (cancelled) return;
        if (response.ok && data.success) {
          await refreshSession();
          await new Promise((r) => setTimeout(r, 400));
          navigate('/portal/dashboard');
        } else {
          autoLoginAttempted.current = false;
          setError(data.error || 'Could not sign in automatically. Use the form below.');
        }
      } catch (err) {
        if (!cancelled) {
          autoLoginAttempted.current = false;
          setError('Could not sign in automatically. Use the form below.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, refreshSession]);

  // Handle auth errors from URL
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const errorMessages = {
        expired: 'This login link has expired. Please request a new one.',
        invalid: 'Invalid login link. Please request a new one.',
        server: 'Server error. Please try again later.',
        unauthorized: 'Email and address do not match our records. Please try again.'
      };
      setError(errorMessages[errorParam] || 'An error occurred. Please try again.');
    }
  }, [searchParams]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/portal/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!address.trim()) {
      setError('Property Address is required');
      return;
    }
    if (!name.trim()) {
      setError('Your name is required');
      return;
    }
    const em = email.trim();
    if (!em || !em.includes('@')) {
      setError('Enter the email address on your invoice (required for security).');
      return;
    }

    setLoading(true);
    setError(null);
    setErrorIsInfo(false);
    setMultipleMatches(null);

    try {
      const response = await fetch(`${BASE_URL}/api/portal/direct-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          address: address.trim(),
          email: email.trim() || undefined
        })
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {}

      if (response.ok && data.success) {
        console.log('[PortalLogin] Login successful, refreshing session...');

        await refreshSession();
        console.log('[PortalLogin] Session refreshed, navigating to dashboard...');
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/portal/dashboard');
      } else if (
        response.ok &&
        data.code === 'DUPLICATE_NAME_AT_ADDRESS' &&
        data.multipleMatches &&
        data.multipleMatches.length > 1
      ) {
        setMultipleMatches(data.multipleMatches);
        setError(null);
      } else {
        setError(data.error || 'Information does not match our records. Please try again.');
        setErrorIsInfo(data.code === 'EMAIL_REQUIRED');
      }
    } catch (err) {
      console.error('[PortalLogin] Login error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCustomer = async (selectedCustomer) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BASE_URL}/api/portal/direct-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: selectedCustomer.name,
          address: address.trim(),
          email: selectedCustomer.email
        })
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {}

      if (response.ok && data.success) {
        setMultipleMatches(null);
        await refreshSession();
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/portal/dashboard');
      } else {
        setError(data.error || 'Could not sign in. Please try again.');
      }
    } catch (err) {
      console.error('[PortalLogin] Error selecting account:', err);
      setError('Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Same static diagonal layer as App.js main shell (dark) */}
      <div aria-hidden="true" style={styles.diagonalBackground}>
        <div aria-hidden="true" style={styles.diagonalBar} />
      </div>
      <div style={styles.inner}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <div style={styles.logoIcon}>🔧</div>
            <h1 style={styles.title}>Customer Portal</h1>
          </div>

          <>
          <p style={styles.subtitle}>
            Enter the name, email, and address on your invoice to access your account
          </p>

          {error && (
            <div style={errorIsInfo ? styles.infoBox : styles.errorBox}>
              <span style={styles.errorIcon}>{errorIsInfo ? 'ℹ️' : '⚠️'}</span>
              {error}
            </div>
          )}

          {multipleMatches && multipleMatches.length > 1 && (
            <div style={styles.multipleMatchesBox}>
              <h3 style={styles.matchesTitle}>More than one account shares this name and address</h3>
              <p style={styles.matchesSubtitle}>Pick the row that matches your email on file:</p>
              <div style={styles.matchesList}>
                {multipleMatches.map((customer, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectCustomer(customer)}
                    disabled={loading}
                    style={styles.matchButton}
                  >
                    <span style={styles.matchName}>{customer.name}</span>
                    <span style={styles.matchEmail}>{customer.email}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Property Address *</label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                placeholder="Start typing your address..."
                style={styles.input}
              />
              <p style={styles.hint}>Must match the address on your invoice/job (required)</p>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Your Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="As shown on your invoice"
                style={styles.input}
              />
              <p style={styles.hint}>Must match the name on your invoice/job (required)</p>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email Address *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Same as on your invoice"
                style={styles.input}
              />
              <p style={styles.hint}>
                Required — we match this to your job so only you can open your portal
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !address.trim() || !name.trim() || !email.trim().includes('@')}
              style={{
                ...styles.button,
                ...(loading || !address.trim() || !name.trim() || !email.trim().includes('@')
                  ? styles.buttonDisabled
                  : {})
              }}
            >
              {loading ? (
                <span style={styles.loadingText}>
                  <span style={styles.spinner}></span>
                  Accessing...
                </span>
              ) : (
                'Access My Account'
              )}
            </button>
          </form>

          <div style={styles.securityNote}>
            <span style={styles.lockIcon}>🔒</span>
            <span>
              Sign-in uses your invoice email plus name and address — your email must match a job at that address
            </span>
          </div>
        </>

          <div style={styles.footer}>
            <a href="/" style={styles.backLink}>
              ← Back to TradeLynx
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    position: 'relative',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  diagonalBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
    overflow: 'hidden'
  },
  diagonalBar: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '340%',
    height: '260px',
    transform: 'translate(-50%, -50%) rotate(-12deg)',
    transformOrigin: 'center',
    background:
      'linear-gradient(90deg, rgba(220,230,245,0) 0%, rgba(220,230,245,0.28) 8%, rgba(220,230,245,0.78) 18%, rgba(100,115,135,0.45) 30%, rgba(100,115,135,0.45) 70%, rgba(220,230,245,0.78) 82%, rgba(220,230,245,0.28) 92%, rgba(220,230,245,0) 100%)',
    filter: 'blur(80px)',
    overflow: 'hidden'
  },
  inner: {
    position: 'relative',
    zIndex: 1,
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    boxSizing: 'border-box'
  },
  card: {
    background: 'rgba(255, 255, 255, 0.35)',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '420px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: '1px solid rgba(255, 255, 255, 0.45)'
  },
  logo: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  logoIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  },
  subtitle: {
    textAlign: 'center',
    color: '#000000',
    marginBottom: '24px',
    fontSize: '15px',
    lineHeight: '1.5'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
    boxSizing: 'border-box'
  },
  hint: {
    fontSize: '12px',
    color: '#000000',
    margin: '4px 0 0 0'
  },
  button: {
    background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    color: 'white',
    border: 'none',
    padding: '14px 24px',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.1s, box-shadow 0.2s',
    boxShadow: '0 4px 6px -1px rgba(249, 115, 22, 0.3)',
    marginTop: '8px'
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none'
  },
  loadingText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px'
  },
  errorIcon: {
    fontSize: '16px'
  },
  infoBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1e40af',
    padding: '12px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '16px'
  },
  multipleMatchesBox: {
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px'
  },
  matchesTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0369a1',
    margin: '0 0 8px 0'
  },
  matchesSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 16px 0'
  },
  matchesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  matchButton: {
    background: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    padding: '14px 16px',
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    transition: 'all 0.2s'
  },
  matchName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827'
  },
  matchEmail: {
    fontSize: '13px',
    color: '#6b7280'
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '20px',
    padding: '12px',
    background: '#f3f4f6',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#6b7280'
  },
  lockIcon: {
    fontSize: '14px'
  },
  footer: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'center'
  },
  backLink: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'color 0.2s',
    ':hover': {
      color: '#374151'
    }
  }
};

// Add CSS animation for spinner
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default PortalLogin;
